import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Play,
  Package,
  AlertTriangle,
  Clock,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  TrendingUp,
  TrendingDown,
  RotateCcw,
  PackagePlus,
  Minus,
  Eye,
  MessageSquare,
  Camera,
  ExternalLink,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, addDays } from "date-fns";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import LogWasteModal from "@/components/LogWasteModal";
import LogPickModal from "@/components/LogPickModal";
import LogReturnModal from "@/components/LogReturnModal";
import { gramsToPounds } from "@/utils/productionCalculations";
import { DndContext, useDraggable, useDroppable, MouseSensor, TouchSensor, useSensor, useSensors, DragOverlay } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to format BOM quantity display (no conversion needed - everything is in native units)
const formatBOMQuantity = (qty, unit, itemType) => {
  return `${qty.toFixed(2)} ${unit}`;
};

// Calculate actual usage from picks and returns
const calculateActualUsage = (lots) => {
  if (!lots || lots.length === 0) return 0;
  
  const totalPicks = lots.reduce((sum, lot) => {
    const picks = lot.picks || [];
    return sum + picks.reduce((pickSum, pick) => pickSum + pick.picked_qty, 0);
  }, 0);
  
  const totalReturns = lots.reduce((sum, lot) => {
    const returns = lot.returns || [];
    return sum + returns.reduce((retSum, ret) => retSum + ret.returned_qty, 0);
  }, 0);
  
  return totalPicks - totalReturns;
};

// Calculate theoretical usage based on BOM and actual production
const calculateTheoreticalUsage = (plannedQty, actualProduced, totalPlanned) => {
  // If no actual production yet, theoretical is 0
  if (!actualProduced || actualProduced === 0) return 0;
  
  // Theoretical = (BOM quantity / planned quantity) * actual produced
  return (plannedQty / totalPlanned) * actualProduced;
};

// Calculate yield percentage
const calculateYield = (actual, theoretical) => {
  // If no actual usage yet (production hasn't started), show 0
  if (!actual || actual === 0) return 0;
  
  // If no theoretical usage yet (production hasn't started), show 0
  if (!theoretical || theoretical === 0) return 0;
  
  // Yield % = (theoretical usage / actual usage) * 100
  // This represents efficiency: higher % = more efficient (less waste)
  // If actual < theoretical: yield > 100% (efficient)
  // If actual > theoretical: yield < 100% (wasteful)
  return ((theoretical / actual) * 100).toFixed(2);
};

// Get yield color
const getYieldColor = (yieldPct) => {
  // If 0, production hasn't started yet - show gray
  if (yieldPct === 0 || yieldPct === '0.00') return 'text-gray-400';
  
  // Normal yield color coding
  if (yieldPct > 97.5) return 'text-green-600';
  if (yieldPct >= 90) return 'text-orange-500';
  return 'text-red-600';
};

// Generate dummy UPM data with actual timestamps (optimized)
const generateUPMData = (wo, timeScale = 'last4hours') => {
  // Calculate target UPM
  const startTime = wo.planned_start_time.split(':').map(Number);
  const endTime = wo.planned_end_time.split(':').map(Number);
  const plannedMinutes = (endTime[0] * 60 + endTime[1]) - (startTime[0] * 60 + startTime[1]);
  
  const unitsPerCase = wo.units_per_case || 12;
  const targetUPM = (wo.planned_quantity * unitsPerCase) / plannedMinutes;
  
  const now = new Date();
  let dataPoints, minutesBack, interval;
  
  // Reduce data points for performance
  switch(timeScale) {
    case 'today':
      minutesBack = (now.getHours() - 6) * 60 + now.getMinutes();
      dataPoints = Math.min(minutesBack / 5, 60); // Max 60 points, every 5 minutes
      interval = 5;
      break;
    case 'last4hours':
      minutesBack = 240;
      dataPoints = 48; // Every 5 minutes
      interval = 5;
      break;
    case 'lasthour':
      minutesBack = 60;
      dataPoints = 30; // Every 2 minutes
      interval = 2;
      break;
    default:
      minutesBack = 240;
      dataPoints = 48;
      interval = 5;
  }
  
  if (!wo.actual_quantity || wo.actual_quantity === 0) {
    return Array.from({ length: dataPoints }, (_, i) => {
      const timestamp = new Date(now.getTime() - (dataPoints - i) * interval * 60000);
      return { 
        time: timestamp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
        upm: 0,
        target: targetUPM
      };
    });
  }
  
  // Single array with color determined by variance
  return Array.from({ length: dataPoints }, (_, i) => {
    const timestamp = new Date(now.getTime() - (dataPoints - i) * interval * 60000);
    const actualUPM = Math.max(0, targetUPM + (Math.random() - 0.5) * (targetUPM * 0.3));
    
    return {
      time: timestamp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
      upm: actualUPM,
      target: targetUPM
    };
  });
};

// Generate dummy hourly production data
const generateHourlyData = (wo) => {
  // Calculate target cases per hour
  const startTime = wo.planned_start_time.split(':').map(Number);
  const endTime = wo.planned_end_time.split(':').map(Number);
  const plannedHours = (endTime[0] * 60 + endTime[1] - startTime[0] * 60 - startTime[1]) / 60;
  const targetCasesPerHour = wo.planned_quantity / plannedHours;
  
  if (!wo.actual_quantity || wo.actual_quantity === 0) {
    return Array.from({ length: 8 }, (_, i) => ({ 
      hour: `${i+6}:00`, 
      cases: 0, 
      target: targetCasesPerHour
    }));
  }
  
  const currentHour = new Date().getHours();
  const startHour = 6;
  
  return Array.from({ length: 8 }, (_, i) => {
    const hour = startHour + i;
    let cases = 0;
    
    if (hour < currentHour) {
      cases = Math.floor(targetCasesPerHour + (Math.random() - 0.3) * (targetCasesPerHour * 0.25)); 
    } else if (hour === currentHour) {
      const minutesIntoHour = new Date().getMinutes();
      cases = Math.floor((targetCasesPerHour / 60) * minutesIntoHour);
    }
    
    return {
      hour: `${hour}:00`,
      cases,
      target: targetCasesPerHour
    };
  });
};

const ProductionDashboard = () => {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWO, setSelectedWO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWasteModal, setShowWasteModal] = useState(false);
  const [wasteModalData, setWasteModalData] = useState(null);
  const [showPickModal, setShowPickModal] = useState(false);
  const [pickModalData, setPickModalData] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnModalData, setReturnModalData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date()); // Current date for navigation
  const [expandedWO, setExpandedWO] = useState(null); // For accordion view
  const [chartTimeScale, setChartTimeScale] = useState('last4hours'); // today, last4hours, lasthour
  const [commentText, setCommentText] = useState('');
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoCaption, setPhotoCaption] = useState('');
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [workOrderDates, setWorkOrderDates] = useState(new Set()); // Set of date strings with work orders

  // Drag-and-drop: sensors and state
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );
  const [activeWO, setActiveWO] = useState(null);
  const [overZone, setOverZone] = useState(null);

  // Droppable targets for quick reschedule to previous/next day (minimal UI change)
  const { isOver: isOverPrev, setNodeRef: setPrevRef } = useDroppable({ id: 'prev-day' });
  const { isOver: isOverNext, setNodeRef: setNextRef } = useDroppable({ id: 'next-day' });

  const onDragStart = (event) => {
    const id = event?.active?.id;
    const wo = workOrders.find(w => w.wo_id === id);
    setActiveWO(wo || null);
    console.log('[DnD] Drag start:', id);
  };

  const onDragOver = (event) => {
    const overId = event?.over?.id || null;
    setOverZone(overId);
  };

  const onDragEnd = async (event) => {
    try {
      const id = event?.active?.id;
      const overId = event?.over?.id || null;
      setOverZone(null);

      if (!id || !overId) {
        setActiveWO(null);
        return;
      }

      let targetDate = new Date(selectedDate);
      if (overId === 'prev-day') {
        targetDate.setDate(targetDate.getDate() - 1);
      } else if (overId === 'next-day') {
        targetDate.setDate(targetDate.getDate() + 1);
      } else {
        // No valid drop target: do nothing
        setActiveWO(null);
        return;
      }

      const newDateStr = targetDate.toISOString().split('T')[0];
      console.log(`[DnD] Reschedule ${id} -> ${newDateStr}`);

      await axios.put(`${API}/work-orders/${id}/reschedule`, { new_date: newDateStr });
      toast.success(`Work order moved to ${newDateStr}`);

      // Show target date after move and refresh
      setSelectedDate(targetDate);
      await fetchWorkOrdersForDate(targetDate);
    } catch (e) {
      console.error('[DnD] Reschedule failed:', e);
      toast.error('Failed to reschedule work order');
    } finally {
      setActiveWO(null);
    }
  };

  // Lightweight draggable wrapper to avoid changing card markup
  const DraggableWrap = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
    const style = {
      transform: transform ? CSS.Translate.toString(transform) : undefined,
      opacity: isDragging ? 0.6 : 1,
      cursor: isDragging ? 'grabbing' : 'grab',
      zIndex: isDragging ? 1000 : 'auto',
    };
    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {children}
      </div>
    );
  };

  // Fetch work orders for selected date
  useEffect(() => {
    fetchWorkOrdersForDate(selectedDate);
  }, [selectedDate]);

  // Set up WebSocket for real-time updates (independent of selectedDate)
  useEffect(() => {
    const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${WS_URL}/api/ws`);

    ws.onopen = () => {
      console.log('[ProductionDashboard] WebSocket connected to:', `${WS_URL}/api/ws`);
    };

    ws.onerror = (error) => {
      console.error('[ProductionDashboard] WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('[ProductionDashboard] WebSocket connection closed');
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      console.log('[ProductionDashboard] WebSocket message received:', message);

      if (['production_started', 'pallet_logged', 'waste_logged', 'downtime_logged', 'lot_allocated', 'work_order_created', 'work_order_updated', 'work_order_deleted', 'production_completed'].includes(message.type)) {
        console.log('[ProductionDashboard] Refetching work orders due to:', message.type);
        fetchWorkOrdersForDate(selectedDate);
      }
    };

    return () => {
      console.log('[ProductionDashboard] Cleaning up WebSocket connection');
      ws.close();
    };
  }, []); // Empty dependency array - WebSocket persists across date changes

  // Fetch work order dates when calendar modal opens
  useEffect(() => {
    const fetchWorkOrderDates = async () => {
      if (showCalendarModal) {
        try {
          // Fetch all work orders to check which dates have orders
          const response = await axios.get(`${API}/work-orders`);
          const dates = new Set();
          
          response.data.forEach(wo => {
            if (wo.planned_date) {
              // Store just the date string (YYYY-MM-DD)
              dates.add(wo.planned_date);
            }
          });
          
          setWorkOrderDates(dates);
        } catch (error) {
          console.error('Error fetching work order dates:', error);
        }
      }
    };
    
    fetchWorkOrderDates();
  }, [showCalendarModal]);

  const fetchWorkOrdersForDate = async (date) => {
    try {
      // Format date without timezone conversion to avoid day shift
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`; // Format: YYYY-MM-DD
      console.log('[ProductionDashboard] Fetching work orders for date:', dateStr);
      
      // Fetch all work orders
      const response = await axios.get(`${API}/work-orders`);
      console.log('[ProductionDashboard] Total work orders received:', response.data.length);
      console.log('[ProductionDashboard] All work orders:', response.data.map(wo => ({ wo_number: wo.wo_number, planned_date: wo.planned_date })));
      
      // Filter out junk/orphaned records (wo_number = "N/A" with no netsuite_internal_id)
      const validOrders = response.data.filter(wo => !(wo.wo_number === 'N/A' && !wo.netsuite_internal_id));
      console.log('[ProductionDashboard] Valid work orders after cleanup:', validOrders.length);
      
      // Deduplicate by wo_number: keep only the most recently updated version
      const seen = new Map();
      const deduplicatedOrders = [];
      for (const wo of validOrders) {
        const key = wo.wo_number || wo.wo_id;
        if (!seen.has(key)) {
          seen.set(key, wo);
          deduplicatedOrders.push(wo);
        } else {
          const existing = seen.get(key);
          // If this one is more recently updated, replace it
          if (wo.updated_at && (!existing.updated_at || wo.updated_at > existing.updated_at)) {
            const idx = deduplicatedOrders.indexOf(existing);
            if (idx >= 0) {
              deduplicatedOrders[idx] = wo;
              seen.set(key, wo);
            }
          }
        }
      }
      console.log('[ProductionDashboard] Work orders after deduplication:', deduplicatedOrders.length);
      
      // Filter work orders: show only work orders for the selected date
      const filteredOrders = deduplicatedOrders.filter(wo => {
        const matches = wo.planned_date === dateStr;
        console.log(`[ProductionDashboard] Comparing "${wo.planned_date}" === "${dateStr}" => ${matches}`);
        return matches;
      });
      
      console.log('[ProductionDashboard] Filtered work orders for date:', filteredOrders.length);
      
      // Sort by planned_date (most recent first), putting empty dates at the end
      filteredOrders.sort((a, b) => {
        // Handle empty dates
        const dateA = a.planned_date && a.planned_date !== '' ? new Date(a.planned_date) : new Date('1970-01-01');
        const dateB = b.planned_date && b.planned_date !== '' ? new Date(b.planned_date) : new Date('1970-01-01');
        return dateB - dateA;
      });
      
      console.log('[ProductionDashboard] Filtered work orders:', filteredOrders.length);
      setWorkOrders(filteredOrders);
      
      // Auto-select first work order if none selected
      if (!selectedWO && filteredOrders.length > 0) {
        setSelectedWO(filteredOrders[0]);
      }
    } catch (error) {
      console.error("[ProductionDashboard] Error fetching work orders:", error);
      toast.error("Failed to fetch work orders");
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Generate calendar grid for the modal
  const generateCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday

    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add the days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  // Check if a date has work orders by checking actual data
  const hasWorkOrders = (year, month, day) => {
    // Format date as YYYY-MM-DD to match backend format
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return workOrderDates.has(dateStr);
  };

  const selectCalendarDate = (day) => {
    if (day) {
      const newDate = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), day);
      setSelectedDate(newDate);
      setShowCalendarModal(false);
    }
  };

  const previousMonth = () => {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCalendarDate(newDate);
  };

  const nextMonth = () => {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCalendarDate(newDate);
  };

  const handleStartProduction = async (woId) => {
    try {
      await axios.post(`${API}/production/${woId}/start`);
      toast.success("Production has been started for this work order");
      fetchWorkOrdersForDate(selectedDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start production");
    }
  };

  const handleLogPallet = async (woId) => {
    const cases = prompt("Enter number of cases in pallet:");
    if (cases && !isNaN(cases)) {
      try {
        await axios.post(`${API}/production/${woId}/log-pallet`, {
          cases: parseFloat(cases)
        });
        toast.success(`Logged ${cases} cases`);
        fetchWorkOrdersForDate(selectedDate);
      } catch (error) {
        toast.error("Failed to log pallet");
      }
    }
  };

  const handleLogDowntime = async (woId) => {
    const duration = prompt("Enter downtime duration (minutes):");
    const reason = prompt("Enter reason code (machine_down, missing_ingredients, food_safety):");
    const description = prompt("Enter description:");
    
    if (duration && reason && description) {
      try {
        await axios.post(`${API}/production/${woId}/log-downtime`, {
          duration_minutes: parseFloat(duration),
          reason_code: reason,
          reason_description: description,
          logged_by: "manual"
        });
        toast.success(`Logged ${duration} minutes of downtime`);
        fetchWorkOrdersForDate(selectedDate);
      } catch (error) {
        toast.error("Failed to log downtime");
      }
    }
  };

  const handleCompleteProduction = async (woId, woNumber) => {
    const confirmed = window.confirm(
      `Are you sure you want to complete production for ${woNumber}?\n\nThis will timestamp the completion and mark the work order as complete.`
    );
    
    if (confirmed) {
      try {
        await axios.post(`${API}/production/${woId}/complete`);
        toast.success(`${woNumber} has been marked as complete`);
        fetchWorkOrdersForDate(selectedDate);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to complete production");
      }
    }
  };

  const handleOpenWasteModal = (wo, component) => {
    setWasteModalData({ wo, component });
    setShowWasteModal(true);
  };

  const handleOpenDetails = (wo) => {
    // Toggle accordion
    if (expandedWO === wo.wo_id) {
      setExpandedWO(null);
    } else {
      setExpandedWO(wo.wo_id);
    }
  };

  const handleOpenPickModal = (wo, component) => {
    setPickModalData({ wo, component });
    setShowPickModal(true);
  };

  const handleOpenReturnModal = (wo, component) => {
    setReturnModalData({ wo, component });
    setShowReturnModal(true);
  };

  const handleAddComment = (wo) => {
    setSelectedWO(wo);
    setCommentText('');
    setShowCommentDialog(true);
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim() || !selectedWO) return;
    
    try {
      await axios.post(`${API}/production/${selectedWO.wo_id}/add-comment?comment_text=${encodeURIComponent(commentText)}&created_by=user`);
      toast.success('Comment added');
      setShowCommentDialog(false);
      setCommentText('');
      fetchWorkOrdersForDate(selectedDate);
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleAddPhoto = (wo) => {
    setSelectedWO(wo);
    setPhotoFile(null);
    setPhotoPreview('');
    setPhotoCaption('');
    setShowPhotoDialog(true);
  };

  const handlePhotoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitPhoto = async () => {
    if (!photoFile || !selectedWO) return;
    
    try {
      // 1. Upload to S3 first
      const toastId = toast.loading("Uploading photo...");
      const formData = new FormData();
      formData.append('file', photoFile);

      // Use the new /upload endpoint
      const uploadRes = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const s3Url = uploadRes.data.url;

      // 2. Send the S3 URL to the backend (instead of the huge Base64 string)
      await axios.post(`${API}/production/${selectedWO.wo_id}/add-photo`, null, {
        params: {
          photo_url: s3Url,
          caption: photoCaption,
          uploaded_by: "user"
        }
      });

      toast.dismiss(toastId);
      toast.success('Photo added successfully');
      
      // Cleanup
      setShowPhotoDialog(false);
      setPhotoFile(null);
      setPhotoPreview('');
      setPhotoCaption('');
      
      // Refresh Data
      fetchWorkOrdersForDate(selectedDate);

    } catch (error) {
      console.error(error);
      toast.error('Failed to upload photo');
    }
  };

  const handleViewDetailsPage = (wo) => {
    // Navigate to work order detail page with referrer info
    navigate(`/work-order/${wo.wo_id}?from=dashboard`);
  };

  const handleLogWaste = async (componentId, wasteQty, reason) => {
    try {
      // Convert pounds to grams if needed
      const component = wasteModalWO.bom_usage.find(c => c.component_id === componentId);
      let wasteQtyToSend = wasteQty;
      
      if (component.item_type === 'food' && component.unit === 'g') {
        // User entered pounds, convert to grams
        wasteQtyToSend = wasteQty * 453.592;
      }
      
      await axios.post(`${API}/production/${wasteModalWO.wo_id}/log-waste`, {
        component_id: componentId,
        waste_qty: wasteQtyToSend,
        waste_reason: reason
      });
      
      toast.success(`Logged ${wasteQty} ${component.item_type === 'food' && component.unit === 'g' ? 'lbs' : component.unit} of waste for ${component.component_name}`);
      
      fetchWorkOrdersForDate(selectedDate);
    } catch (error) {
      toast.error("Failed to log waste");
      throw error;
    }
  };

  const handleLogPick = async (lotId, pickedQty) => {
    try {
      await axios.post(`${API}/lots/${lotId}/pick`, {
        picked_qty: pickedQty,
        wo_id: pickModalData.wo.wo_id
      });
      
      toast.success(`Logged ${pickedQty} picked from lot`);
      fetchWorkOrdersForDate(selectedDate);
    } catch (error) {
      toast.error("Failed to log pick");
      throw error;
    }
  };

  const handleLogReturn = async (lotId, returnedQty, reason) => {
    try {
      await axios.post(`${API}/lots/${lotId}/return`, {
        returned_qty: returnedQty,
        return_reason: reason,
        wo_id: returnModalData.wo.wo_id
      });
      
      toast.success(`Logged ${returnedQty} returned to lot`);
      fetchWorkOrdersForDate(selectedDate);
    } catch (error) {
      toast.error("Failed to log return");
      throw error;
    }
  };

  // Calculate pace - based on actual start time and production rate
  const calculatePace = (wo) => {
    if (wo.status !== 'in_progress' || !wo.actual_start_time) {
      return { status: 'not_started', percentage: 0 };
    }

    const now = new Date();
    const [actualStartHour, actualStartMin] = wo.actual_start_time.split(':').map(Number);
    const [planStartHour, planStartMin] = wo.planned_start_time.split(':').map(Number);
    const [planEndHour, planEndMin] = wo.planned_end_time.split(':').map(Number);
    
    // Calculate elapsed time since ACTUAL start (minutes)
    const actualStartTime = new Date();
    actualStartTime.setHours(actualStartHour, actualStartMin, 0);
    const elapsedMinutes = (now - actualStartTime) / (1000 * 60);
    
    // Calculate planned duration (how long production should take)
    const plannedDuration = (planEndHour * 60 + planEndMin) - (planStartHour * 60 + planStartMin);
    
    // Calculate expected production rate (cases per minute)
    const expectedRate = wo.planned_quantity / plannedDuration;
    
    // Calculate actual production rate (cases per minute)
    const actualQty = wo.actual_quantity || 0;
    const actualRate = elapsedMinutes > 0 ? actualQty / elapsedMinutes : 0;
    
    // Calculate pace percentage (comparing actual rate to expected rate)
    const pacePercentage = expectedRate > 0 ? (actualRate / expectedRate) * 100 : 0;
    
    // Calculate how much should have been produced by now at expected rate
    const expectedQtyNow = expectedRate * elapsedMinutes;
    
    let status = 'on_pace';
    if (pacePercentage >= 105) status = 'ahead';
    else if (pacePercentage < 95) status = 'behind';
    
    return { 
      status, 
      percentage: pacePercentage.toFixed(1), 
      actualQty, 
      expectedQty: expectedQtyNow.toFixed(0),
      actualRate: actualRate.toFixed(1),
      expectedRate: expectedRate.toFixed(1)
    };
  };

  const getPaceIndicator = (wo) => {
    const pace = calculatePace(wo);
    
    if (wo.status === 'scheduled') {
      return (
        <div className="flex items-center gap-2 text-gray-500">
          <Minus className="w-5 h-5" />
          <span className="font-semibold">Not Started</span>
        </div>
      );
    }
    
    if (wo.status === 'completed') {
      return (
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle className="w-5 h-5" />
          <span className="font-semibold">Completed</span>
        </div>
      );
    }

    if (pace.status === 'ahead') {
      return (
        <div className="flex items-center gap-2 text-green-600">
          <TrendingUp className="w-5 h-5" />
          <span className="font-semibold">Ahead ({pace.percentage}%)</span>
        </div>
      );
    } else if (pace.status === 'behind') {
      return (
        <div className="flex items-center gap-2 text-red-600">
          <TrendingDown className="w-5 h-5" />
          <span className="font-semibold">Behind ({pace.percentage}%)</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2 text-blue-600">
          <Minus className="w-5 h-5" />
          <span className="font-semibold">On Pace ({pace.percentage}%)</span>
        </div>
      );
    }
  };

  if (loading) {
    return <div className="p-8">Loading production dashboard...</div>;
  }

  const isToday = selectedDate.toDateString() === new Date().toDateString();

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragOver={onDragOver} onDragEnd={onDragEnd}>
    <div className="production-dashboard" style={{ padding: '1.5rem', background: '#f8f9fa', minHeight: '100vh' }}>
      <div className="dashboard-header" style={{ marginBottom: '1.5rem' }}>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold">Production Dashboard</h1>
          
          {/* Date Navigation */}
          <div className="flex items-center gap-3">
            <div
              ref={setPrevRef}
              className={`rounded-md transition-colors ${activeWO ? 'ring-2 ring-transparent' : ''} ${isOverPrev ? 'ring-blue-500 bg-blue-50' : ''}`}
              title="Drop here to move to previous day"
            >
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousDay}
                className="px-3"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Button>
            </div>
            
            <button 
              onClick={() => setShowCalendarModal(true)}
              className="text-center min-w-[200px] hover:bg-gray-50 rounded-lg px-3 py-1 transition-colors cursor-pointer"
            >
              <div className="text-lg font-semibold">
                {selectedDate.toLocaleDateString('en-US', { weekday: 'long' })}
              </div>
              <div className="text-sm text-gray-600">
                {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            </button>
            
            <div
              ref={setNextRef}
              className={`rounded-md transition-colors ${activeWO ? 'ring-2 ring-transparent' : ''} ${isOverNext ? 'ring-blue-500 bg-blue-50' : ''}`}
              title="Drop here to move to next day"
            >
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextDay}
                className="px-3"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Button>
            </div>
            
            {!isToday && (
              <Button 
                variant="default" 
                size="sm"
                onClick={goToToday}
                className="ml-2"
              >
                Today
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="work-orders-section">
        {/* Work Orders List */}
        {workOrders.length === 0 ? (
            <Card>
              <CardContent className="pt-8 pb-8 text-center">
                <p className="text-gray-500 mb-4">No work orders scheduled for this date</p>
                <p className="text-sm text-gray-400">
                  {isToday 
                    ? "Work orders will appear here when scheduled for today" 
                    : "Use date navigation to view other dates"
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
            {workOrders.map(wo => (
              <DraggableWrap id={wo.wo_id}>
              <Card
                key={wo.wo_id}
                className={`cursor-pointer transition-all hover:shadow-lg ${selectedWO?.wo_id === wo.wo_id ? 'border-2 border-blue-500' : ''}`}
                onClick={() => setSelectedWO(wo)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{wo.wo_number} - {wo.production_line}</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">{wo.item_name}</p>
                    </div>
                    <Badge className={wo.status === 'completed' ? 'bg-green-600' : wo.status === 'in_progress' ? 'bg-blue-600' : 'bg-gray-500'}>
                      {wo.status.replace('_', ' ')}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div>
                      <div className="text-xs text-gray-500">Planned</div>
                      <div className="font-semibold">{wo.planned_quantity} cases</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Actual</div>
                      <div className="font-semibold text-blue-600">{wo.actual_quantity || 0} cases</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Time</div>
                      <div className="font-semibold">{wo.planned_start_time} - {wo.planned_end_time}</div>
                    </div>
                  </div>

                  {/* Yield KPIs and Status Indicator - Only show if BOM data exists */}
                  {wo.bom_usage && wo.bom_usage.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {/* Ingredient Yield */}
                      {(() => {
                        const ingredients = wo.bom_usage.filter(c => c.item_type === 'ingredient');
                        let totalActual = 0;
                        let totalTheoretical = 0;
                        
                        ingredients.forEach(component => {
                          const actualUsage = calculateActualUsage(component.lots);
                          const theoreticalUsage = calculateTheoreticalUsage(
                            component.planned_qty, 
                            wo.actual_quantity || 0, 
                            wo.planned_quantity
                          );
                          totalActual += actualUsage;
                          totalTheoretical += theoreticalUsage;
                        });
                        
                        const overallYield = totalTheoretical > 0 
                          ? Math.round((totalTheoretical / totalActual) * 100) 
                          : 0;
                        
                        return overallYield > 0 ? (
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-2 rounded">
                            <div className="text-xs text-gray-600">Ingredient Yield</div>
                            <div className={`text-lg font-bold ${getYieldColor(overallYield)}`}>
                              {overallYield}%
                            </div>
                          </div>
                        ) : null;
                      })()}

                      {/* Packaging Yield */}
                      {(() => {
                        const packaging = wo.bom_usage.filter(c => c.item_type === 'packaging');
                        let totalActual = 0;
                        let totalTheoretical = 0;
                        
                        packaging.forEach(component => {
                          const actualUsage = calculateActualUsage(component.lots);
                          const theoreticalUsage = calculateTheoreticalUsage(
                            component.planned_qty, 
                            wo.actual_quantity || 0, 
                            wo.planned_quantity
                          );
                          totalActual += actualUsage;
                          totalTheoretical += theoreticalUsage;
                        });
                        
                        const packagingYield = totalTheoretical > 0 
                          ? Math.round((totalTheoretical / totalActual) * 100) 
                          : 0;
                        
                        return packagingYield > 0 ? (
                          <div className="bg-gradient-to-br from-green-50 to-green-100 p-2 rounded">
                            <div className="text-xs text-gray-600">Packaging Yield</div>
                            <div className={`text-lg font-bold ${getYieldColor(packagingYield)}`}>
                              {packagingYield}%
                            </div>
                          </div>
                        ) : null;
                      })()}

                      {/* Status Indicator */}
                      {(() => {
                        console.log(`Status Indicator for ${wo.wo_number}: status="${wo.status}", type=${typeof wo.status}, bom_usage=${wo.bom_usage ? wo.bom_usage.length : 'null'}`);
                        const pace = calculatePace(wo);
                        let bgColor = 'from-gray-50 to-gray-100';
                        let textColor = 'text-gray-600';
                        let statusText = 'Not Started';
                        
                        if (wo.status === 'completed' || wo.status === 'finalized' || wo.status === 'production_complete') {
                          console.log(`  -> Matched completed condition for ${wo.wo_number}`);
                          bgColor = 'from-green-50 to-green-100';
                          textColor = 'text-green-600';
                          statusText = wo.status === 'completed' ? 'Completed' : 'Complete';
                        } else if (wo.status === 'in_production') {
                          console.log(`  -> Matched in_production condition for ${wo.wo_number}`);
                          if (pace.status === 'ahead') {
                            bgColor = 'from-green-50 to-green-100';
                            textColor = 'text-green-600';
                            statusText = `Ahead ${pace.percentage}%`;
                          } else if (pace.status === 'behind') {
                            bgColor = 'from-red-50 to-red-100';
                            textColor = 'text-red-600';
                            statusText = `Behind ${pace.percentage}%`;
                          } else {
                            bgColor = 'from-blue-50 to-blue-100';
                            textColor = 'text-blue-600';
                            statusText = `On Pace ${pace.percentage}%`;
                          }
                        } else {
                          console.log(`  -> Status "${wo.status}" did not match any condition`);
                        }
                        
                        return (
                          <div className={`bg-gradient-to-br ${bgColor} p-2 rounded`}>
                            <div className="text-xs text-gray-600">Status</div>
                            <div className={`text-lg font-bold ${textColor}`}>
                              {statusText}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <Button 
                      size="sm" 
                      variant={expandedWO === wo.wo_id ? "default" : "outline"}
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        console.log('Details clicked for WO:', wo.wo_id);
                        console.log('Current expandedWO:', expandedWO);
                        handleOpenDetails(wo); 
                      }}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      {expandedWO === wo.wo_id ? 'Hide Details' : 'Show Details'}
                    </Button>
                    
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        handleViewDetailsPage(wo); 
                      }}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View Full Page
                    </Button>

                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        handleAddComment(wo); 
                      }}
                    >
                      <MessageSquare className="w-4 h-4 mr-1" />
                      Add Comment
                    </Button>

                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        handleAddPhoto(wo); 
                      }}
                    >
                      <Camera className="w-4 h-4 mr-1" />
                      Add Photo
                    </Button>
                    
                    {wo.status === 'scheduled' && (
                      <Button size="sm" onClick={(e) => { e.stopPropagation(); handleStartProduction(wo.wo_id); }}>
                        <Play className="w-4 h-4 mr-1" />
                        Start Production
                      </Button>
                    )}
                    {wo.status === 'in_progress' && (
                      <>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleLogPallet(wo.wo_id); }}>
                          <Package className="w-4 h-4 mr-1" />
                          Log Pallet
                        </Button>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleOpenWasteModal(wo); }}>
                          <AlertTriangle className="w-4 h-4 mr-1" />
                          Log Waste
                        </Button>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleLogDowntime(wo.wo_id); }}>
                          <Clock className="w-4 h-4 mr-1" />
                          Log Downtime
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={(e) => { e.stopPropagation(); handleCompleteProduction(wo.wo_id, wo.wo_number); }}
                        >
                          <CheckSquare className="w-4 h-4 mr-1" />
                          Complete Production
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Accordion Details */}
                  {expandedWO === wo.wo_id && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="space-y-4">

                        {/* Real-Time Charts */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* UPM Line Chart */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold">Units Per Minute (Live)</h4>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => setChartTimeScale('today')}
                                  className={`text-xs px-2 py-1 rounded ${chartTimeScale === 'today' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
                                  title="Show today's data"
                                >
                                  📅
                                </button>
                                <button
                                  onClick={() => setChartTimeScale('last4hours')}
                                  className={`text-xs px-2 py-1 rounded ${chartTimeScale === 'last4hours' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
                                  title="Show last 4 hours"
                                >
                                  4h
                                </button>
                                <button
                                  onClick={() => setChartTimeScale('lasthour')}
                                  className={`text-xs px-2 py-1 rounded ${chartTimeScale === 'lasthour' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
                                  title="Show last hour"
                                >
                                  1h
                                </button>
                              </div>
                            </div>
                            <div className="bg-white p-3 rounded border h-32">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={generateUPMData(wo, chartTimeScale)}>
                                  <XAxis 
                                    dataKey="time" 
                                    tick={{ fontSize: 9 }}
                                    interval="preserveEnd"
                                  />
                                  <YAxis 
                                    tick={{ fontSize: 10 }}
                                    domain={[0, 'auto']}
                                  />
                                  <Tooltip 
                                    contentStyle={{ fontSize: 12 }}
                                    formatter={(value, name) => {
                                      if (name === 'upm') return [`${value.toFixed(1)} UPM`, 'Actual'];
                                      if (name === 'target') return [`${value.toFixed(1)} UPM`, 'Target'];
                                      return value;
                                    }}
                                  />
                                  {/* Target line */}
                                  <Line 
                                    type="monotone" 
                                    dataKey="target" 
                                    stroke="#94a3b8" 
                                    strokeWidth={1.5}
                                    strokeDasharray="5 5"
                                    dot={false}
                                    isAnimationActive={false}
                                  />
                                  {/* Actual line - simplified single color */}
                                  <Line 
                                    type="monotone" 
                                    dataKey="upm" 
                                    stroke={wo.actual_quantity > 0 ? '#2563eb' : '#94a3b8'}
                                    strokeWidth={2}
                                    dot={false}
                                    isAnimationActive={false}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          </div>

                          {/* Hourly Production Bar Chart */}
                          <div>
                            <h4 className="text-sm font-semibold mb-2">Hourly Production (Cases)</h4>
                            <div className="bg-white p-3 rounded border h-32">
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={generateHourlyData(wo)}>
                                  <XAxis 
                                    dataKey="hour" 
                                    tick={{ fontSize: 10 }}
                                  />
                                  <YAxis 
                                    tick={{ fontSize: 10 }}
                                  />
                                  <Tooltip 
                                    contentStyle={{ fontSize: 12 }}
                                    formatter={(value, name) => {
                                      if (name === 'cases') return [`${value} cases`, 'Actual'];
                                      if (name === 'target') return [`${Math.round(value)} cases`, 'Target'];
                                      return value;
                                    }}
                                  />
                                  {/* Target line */}
                                  <Line 
                                    type="monotone" 
                                    dataKey="target" 
                                    stroke="#94a3b8" 
                                    strokeWidth={1.5}
                                    strokeDasharray="5 5"
                                    dot={false}
                                    isAnimationActive={false}
                                  />
                                  {/* Bars - simplified coloring */}
                                  <Bar 
                                    dataKey="cases" 
                                    fill="#2563eb"
                                    radius={[4, 4, 0, 0]}
                                    isAnimationActive={false}
                                  />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              </DraggableWrap>
            ))}
          </div>
          )}
        </div>

      {/* Log Waste Modal */}
      <LogWasteModal
        isOpen={showWasteModal}
        onClose={() => setShowWasteModal(false)}
        workOrder={wasteModalData?.wo}
        component={wasteModalData?.component}
        onSuccess={() => fetchWorkOrdersForDate(selectedDate)}
      />

      {/* Log Pick Modal */}
      <LogPickModal
        isOpen={showPickModal}
        onClose={() => setShowPickModal(false)}
        workOrder={pickModalData?.wo}
        component={pickModalData?.component}
        onSuccess={() => fetchWorkOrdersForDate(selectedDate)}
      />

      {/* Log Return Modal */}
      <LogReturnModal
        isOpen={showReturnModal}
        onClose={() => setShowReturnModal(false)}
        workOrder={returnModalData?.wo}
        component={returnModalData?.component}
        onSuccess={() => fetchWorkOrdersForDate(selectedDate)}
      />

      {/* Add Comment Dialog */}
      {showCommentDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Add Comment - {selectedWO?.wo_number}</h3>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Enter your comment..."
              className="w-full p-3 border rounded-md resize-none"
              rows={4}
            />
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSubmitComment} disabled={!commentText.trim()}>
                Add Comment
              </Button>
              <Button variant="outline" onClick={() => setShowCommentDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Photo Dialog */}
      {showPhotoDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Add Photo - {selectedWO?.wo_number}</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="photoFile">Select Photo</Label>
                <input
                  type="file"
                  id="photoFile"
                  accept="image/*"
                  onChange={handlePhotoFileChange}
                  className="w-full p-2 border rounded-md"
                />
                {photoPreview && (
                  <div className="mt-3">
                    <img 
                      src={photoPreview} 
                      alt="Preview" 
                      className="max-w-full h-auto rounded-md border"
                      style={{ maxHeight: '200px' }}
                    />
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="photoCaption">Caption (optional)</Label>
                <Input
                  id="photoCaption"
                  value={photoCaption}
                  onChange={(e) => setPhotoCaption(e.target.value)}
                  placeholder="Enter photo caption..."
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSubmitPhoto} disabled={!photoFile}>
                Add Photo
              </Button>
              <Button variant="outline" onClick={() => setShowPhotoDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Modal */}
      <Dialog open={showCalendarModal} onOpenChange={setShowCalendarModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Date</DialogTitle>
          </DialogHeader>
          
          <div className="p-4">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={previousMonth}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="text-lg font-semibold">
                {calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={nextMonth}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Day Labels */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {generateCalendarDays(calendarDate).map((day, index) => {
                const isToday = day && 
                  calendarDate.getFullYear() === new Date().getFullYear() &&
                  calendarDate.getMonth() === new Date().getMonth() &&
                  day === new Date().getDate();
                
                const isSelected = day && 
                  calendarDate.getFullYear() === selectedDate.getFullYear() &&
                  calendarDate.getMonth() === selectedDate.getMonth() &&
                  day === selectedDate.getDate();
                
                const hasWO = day && hasWorkOrders(
                  calendarDate.getFullYear(), 
                  calendarDate.getMonth(), 
                  day
                );

                return (
                  <button
                    key={index}
                    onClick={() => selectCalendarDate(day)}
                    disabled={!day}
                    className={`
                      aspect-square p-2 text-sm rounded-lg transition-colors
                      ${!day ? 'invisible' : ''}
                      ${isToday ? 'ring-2 ring-blue-500' : ''}
                      ${isSelected ? 'bg-blue-500 text-white font-semibold' : ''}
                      ${!isSelected && hasWO ? 'bg-green-100 hover:bg-green-200' : ''}
                      ${!isSelected && !hasWO && day ? 'hover:bg-gray-100' : ''}
                      ${!day ? '' : 'cursor-pointer'}
                    `}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-4 pt-4 border-t space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <div className="w-6 h-6 bg-green-100 rounded border"></div>
                <span className="text-gray-600">Has work orders</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <div className="w-6 h-6 rounded border-2 border-blue-500"></div>
                <span className="text-gray-600">Today</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <div className="w-6 h-6 bg-blue-500 rounded"></div>
                <span className="text-gray-600">Selected date</span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DragOverlay>
        {activeWO ? (
          <div className="p-2 bg-white rounded shadow border">
            <div className="font-semibold">{activeWO.wo_number}</div>
            <div className="text-sm text-gray-600">{activeWO.item_name}</div>
          </div>
        ) : null}
      </DragOverlay>

    </div>
    </DndContext>
  );
};

export default ProductionDashboard;
